import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import bellOutline from "@iconify-icons/eva/bell-outline";
import bellFill from "@iconify-icons/eva/bell-fill";
import { useDispatch, useSelector } from "src/redux/store";
import { fetchScheduleAlerts } from "src/redux/slices/scheduleAlerts";

// Mapeo de tipos a copy + color. Mantiene la lista corta de alertas que
// realmente afectan al día a día del propietario; el resto (DESCANSO_EXCESO,
// JORNADA_EXCESO) son informativas.
const TYPE_COPY = {
  AUSENTE: {
    label: "Ausente",
    color: "#cf1322",
    severity: 0,
  },
  SIN_CHECKOUT: {
    label: "Sin fichar salida",
    color: "#cf1322",
    severity: 1,
  },
  RETRASO_ENTRADA: {
    label: "Retraso entrada",
    color: "#fa8c16",
    severity: 2,
  },
  JORNADA_EXCESO: {
    label: "Jornada excedida",
    color: "#722ed1",
    severity: 3,
  },
  DESCANSO_EXCESO: {
    label: "Descanso largo",
    color: "#722ed1",
    severity: 4,
  },
};

function describe(alerta) {
  const m = alerta.minutos ?? 0;
  switch (alerta.tipo) {
    case "AUSENTE":
      return m > 0 ? `Falta a su turno (${m} min)` : "Falta a su turno";
    case "RETRASO_ENTRADA":
      return `Llegó ${m} min tarde`;
    case "SIN_CHECKOUT":
      return m > 0
        ? `Sin fichar salida (${m} min después del fin)`
        : "Olvidó fichar la salida";
    case "JORNADA_EXCESO":
      return `Lleva ${Math.floor(m / 60)}h ${m % 60}min trabajadas`;
    case "DESCANSO_EXCESO":
      return `Descanso de ${m} min`;
    default:
      return null;
  }
}

// Hook compartido para el badge y el contenido. Lee del slice
// `scheduleAlerts` (Redux) — antes usaba `useState` local, lo que hacía
// que dos consumidores concurrentes (AlertsCenter + ScheduleAlertsPanel)
// se pisasen via `staleGuard`: uno fetcheaba, el otro veía la key
// marcada y se quedaba con `[]`. Con Redux ambos hooks comparten la
// misma fuente de datos.
export function useScheduleAlertsData() {
  const dispatch = useDispatch();
  const { alertas, loading } = useSelector((s) => s.scheduleAlerts);

  useEffect(() => {
    // Un solo fetch al montar (el thunk tiene staleGuard interno para
    // colapsar remounts). SignalR (NotificationRestauranteHub) cubre el
    // broadcast de cambios de fichaje/horario en tiempo real. El
    // bootstrap ya hidrata este slice en el arranque — el staleGuard
    // hace que este GET sea no-op en ese caso.
    dispatch(fetchScheduleAlerts());
  }, [dispatch]);

  const count = alertas.length;
  const hasCritical = alertas.some(
    (a) => a.tipo === "AUSENTE" || a.tipo === "SIN_CHECKOUT",
  );
  return { alertas, loading, count, hasCritical };
}

export default function ScheduleAlertsBadge() {
  const [anchor, setAnchor] = useState(null);
  const { alertas, loading, count, hasCritical } = useScheduleAlertsData();

  const grouped = useMemo(() => {
    const map = new Map();
    for (const a of alertas) {
      if (!map.has(a.employeeId)) {
        map.set(a.employeeId, {
          id: a.employeeId,
          nombre: a.nombre,
          avatar: a.avatar,
          items: [],
        });
      }
      map.get(a.employeeId).items.push(a);
    }
    // Ordenar cada empleado por severidad de su alerta más grave
    const arr = Array.from(map.values());
    for (const emp of arr) {
      emp.items.sort(
        (a, b) =>
          (TYPE_COPY[a.tipo]?.severity ?? 99) -
          (TYPE_COPY[b.tipo]?.severity ?? 99),
      );
    }
    arr.sort((a, b) => {
      const sa = TYPE_COPY[a.items[0]?.tipo]?.severity ?? 99;
      const sb = TYPE_COPY[b.items[0]?.tipo]?.severity ?? 99;
      return sa - sb;
    });
    return arr;
  }, [alertas]);

  const open = Boolean(anchor);

  return (
    <>
      <Tooltip
        title={
          count === 0
            ? "Sin alertas de fichaje"
            : `${count} alerta${count === 1 ? "" : "s"} de fichaje`
        }
        arrow
      >
        <IconButton
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            color:
              count === 0
                ? "text.secondary"
                : hasCritical
                  ? "error.main"
                  : "warning.main",
          }}
        >
          <Badge
            badgeContent={count}
            color={hasCritical ? "error" : "warning"}
            max={9}
            overlap="circular"
          >
            <Icon icon={count > 0 ? bellFill : bellOutline} width={20} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 360, maxHeight: 480 } }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Alertas de fichaje
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {count === 0
                ? "Sin incidencias hoy"
                : `${count} incidencia${count === 1 ? "" : "s"} hoy`}
            </Typography>
          </Box>
          {loading && (
            <Typography variant="caption" color="text.disabled">
              actualizando…
            </Typography>
          )}
        </Box>

        <Box sx={{ overflowY: "auto", maxHeight: 400 }}>
          {count === 0 && (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Todo en orden hoy.
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                display="block"
                mt={0.5}
              >
                Las alertas se refrescan cada minuto.
              </Typography>
            </Box>
          )}

          {grouped.map((emp) => (
            <Box
              key={emp.id}
              sx={{
                p: 1.5,
                borderBottom: 1,
                borderColor: "divider",
                "&:last-child": { borderBottom: 0 },
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <Avatar
                  src={emp.avatar}
                  sx={{ width: 28, height: 28, fontSize: 12 }}
                >
                  {emp.nombre?.charAt(0)}
                </Avatar>
                <Typography variant="body2" fontWeight={600}>
                  {emp.nombre}
                </Typography>
              </Box>
              {emp.items.map((a, idx) => {
                const cfg = TYPE_COPY[a.tipo] || {
                  label: a.tipo,
                  color: "#666",
                };
                return (
                  <Box
                    key={`${a.tipo}-${a.workScheduleItemId ?? idx}`}
                    sx={{
                      ml: 4.5,
                      py: 0.5,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: cfg.color, fontWeight: 700, fontSize: 11 }}
                    >
                      {cfg.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: 11 }}
                    >
                      {describe(a)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
}

// Panel reutilizable para uso embebido (p.ej. dentro del AlertsCenter
// unificado como tab). Replica el contenido del Popover pero sin el
// Popover en sí, para que el contenedor padre decida cómo mostrarlo.
export function ScheduleAlertsPanel() {
  const { alertas, loading, count } = useScheduleAlertsData();

  const grouped = useMemo(() => {
    const map = new Map();
    for (const a of alertas) {
      if (!map.has(a.employeeId)) {
        map.set(a.employeeId, {
          id: a.employeeId,
          nombre: a.nombre,
          avatar: a.avatar,
          items: [],
        });
      }
      map.get(a.employeeId).items.push(a);
    }
    const arr = Array.from(map.values());
    for (const emp of arr) {
      emp.items.sort(
        (a, b) =>
          (TYPE_COPY[a.tipo]?.severity ?? 99) -
          (TYPE_COPY[b.tipo]?.severity ?? 99),
      );
    }
    arr.sort((a, b) => {
      const sa = TYPE_COPY[a.items[0]?.tipo]?.severity ?? 99;
      const sb = TYPE_COPY[b.items[0]?.tipo]?.severity ?? 99;
      return sa - sb;
    });
    return arr;
  }, [alertas]);

  return (
    <Box>
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Alertas de fichaje
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {count === 0
              ? "Sin incidencias hoy"
              : `${count} incidencia${count === 1 ? "" : "s"} hoy`}
          </Typography>
        </Box>
        {loading && (
          <Typography variant="caption" color="text.disabled">
            actualizando…
          </Typography>
        )}
      </Box>

      <Box sx={{ overflowY: "auto", maxHeight: 400 }}>
        {count === 0 && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Todo en orden hoy.
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              display="block"
              mt={0.5}
            >
              Las alertas se refrescan cada minuto.
            </Typography>
          </Box>
        )}
        {grouped.map((emp) => (
          <Box
            key={emp.id}
            data-testid="schedule-alert-employee"
            data-employee-name={emp.nombre || ""}
            sx={{
              p: 1.5,
              borderBottom: 1,
              borderColor: "divider",
              "&:last-child": { borderBottom: 0 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Avatar src={emp.avatar} sx={{ width: 28, height: 28, fontSize: 12 }}>
                {emp.nombre?.charAt(0)}
              </Avatar>
              <Typography variant="body2" fontWeight={600}>
                {emp.nombre}
              </Typography>
            </Box>
            {emp.items.map((a, idx) => {
              const cfg = TYPE_COPY[a.tipo] || { label: a.tipo, color: "#666" };
              return (
                <Box
                  key={`${a.tipo}-${a.workScheduleItemId ?? idx}`}
                  data-testid="schedule-alert"
                  data-alert-type={a.tipo}
                  sx={{ ml: 4.5, py: 0.5, display: "flex", flexDirection: "column" }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: cfg.color, fontWeight: 700, fontSize: 11 }}
                  >
                    {cfg.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: 11 }}
                  >
                    {describe(a)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
